import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken, useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { Alert, BarChart } from 'grommet-icons';
import { ChartCard } from './ChartCard';
import { EnergyChart } from './EnergyChart';
import { NoFiltersMessage } from './NoFiltersMessage';
import { ApiError } from '/src/api';
import { ApiDataService } from '/src/api/services/ApiDataService';
import {
  ApiDataChartRangePreset,
  ApiDataChartTimeframe,
  ApiDataChartViewQueryState,
  ApiDataFilterQueryState,
  TChartApiDataRequest,
} from '/src/api/types/ApiData';
import { Box, BoxProps, CardHeader, InfoBox, Input, LoadingSpinner, Select, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { useRestorableListState } from '/src/hooks/useRestorableListState';
import { toastMessages } from '/src/lib/toast';
import { pxToRem } from '/src/utils';

const getMinTimeframe = (stationsCount: number): number => {
  if (stationsCount > 200) return Object.values(ApiDataChartTimeframe).indexOf(ApiDataChartTimeframe.years);
  if (stationsCount > 50) return Object.values(ApiDataChartTimeframe).indexOf(ApiDataChartTimeframe.quarters);
  if (stationsCount > 15) return Object.values(ApiDataChartTimeframe).indexOf(ApiDataChartTimeframe.months);
  return Object.values(ApiDataChartTimeframe).indexOf(ApiDataChartTimeframe.days);
};

const presetToDayRange = (
  preset: ApiDataChartRangePreset,
  startDate?: string,
  endDate?: string,
  filterStartDate?: string,
  filterEndDate?: string
): [start?: Dayjs, end?: Dayjs] => {
  let start: Dayjs | undefined;
  let end: Dayjs | undefined;

  if (preset === ApiDataChartRangePreset.custom) {
    start = dayjs(startDate || filterStartDate || undefined); // undefined is here to prevent invalid date if one of params is empty string
    end = dayjs(endDate || filterEndDate || undefined);
  } else {
    start = dayjs(startDate || filterStartDate || undefined);
    switch (preset) {
      case ApiDataChartRangePreset.days90:
        end = start.add(90, 'day').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.months6:
        end = start.add(6, 'month').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year1:
        end = start.add(1, 'year').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year2:
        end = start.add(2, 'year').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year3:
        end = start.add(3, 'year').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year4:
        end = start.add(4, 'year').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year5:
        end = start.add(5, 'year').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year6:
        end = start.add(6, 'year').subtract(1, 'day');
        break;
      case ApiDataChartRangePreset.year7:
        end = start.add(7, 'year').subtract(1, 'day');
        break;

      case ApiDataChartRangePreset.currentPage:
        start = filterStartDate ? dayjs(filterStartDate) : undefined;
        end = filterEndDate ? dayjs(filterEndDate) : undefined;
        break;
      default:
        start = dayjs(startDate || filterStartDate || undefined);
        end = start;
        break;
    }
  }

  return [start, end];
};

export const ChartsView: FC<ChartsViewProps> = (props) => {
  const { filters, hasAppliedFilters, surfaceId, ...boxProps } = props;
  const { t } = useTranslation();
  const userStore = useUserStore();
  const { handleApiError } = useGlobalStore();

  const [draftStations, setDraftStations] = useState<string[]>([]);
  const [stationList, setStationList] = useState<string[]>([]);

  const restorableList = useRestorableListState<ApiDataChartViewQueryState>({
    surfaceId: surfaceId || 'api-data-chart',
    defaults: {
      timeframe: Object.values(ApiDataChartTimeframe)[getMinTimeframe(filters?.serialNumber?.length || 0)],
      rangePreset: ApiDataChartRangePreset.currentPage,
      ...filters,
    },
    standalone: true,
  });

  const queryState = restorableList.state;
  const setQueryState = restorableList.setState;

  const selectedSerialNumbers = queryState.serialNumber || [];
  const isStationsSelected = selectedSerialNumbers.length > 0;
  const appliedSerialNumbers = filters.serialNumber || [];
  const areStationsSynchronized =
    stationList.length === appliedSerialNumbers.length &&
    stationList.every((serialNumber, index) => serialNumber === appliedSerialNumbers[index]) &&
    selectedSerialNumbers.every((serialNumber) => appliedSerialNumbers.includes(serialNumber));
  const canLoadCharts = hasAppliedFilters && isStationsSelected && areStationsSynchronized;

  const requestDates = useMemo(
    () =>
      queryState?.rangePreset
        ? presetToDayRange(
            queryState.rangePreset,
            queryState.startDate,
            queryState.endDate,
            filters.startDate,
            filters.endDate
          )
        : undefined,
    [queryState.rangePreset, queryState.startDate, queryState.endDate, filters.startDate, filters.endDate]
  );

  const request: TChartApiDataRequest = useMemo(() => {
    return {
      ...queryState,
      chargingNetwork: filters.chargingNetwork,
      subOrganization: filters.subOrganization,
      state: filters.state,
      chargerLevel: filters.chargerLevel,
      startDate: requestDates?.[0] ? requestDates[0].format('YYYY-MM-DD') : undefined,
      endDate: requestDates?.[1] ? requestDates[1].format('YYYY-MM-DD') : undefined,
    };
  }, [queryState, requestDates, filters.chargingNetwork, filters.chargerLevel, filters.subOrganization, filters.state]);

  /* React Query */
  const {
    data: queryData,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ['api-data', userStore.user?.id, request],
    queryFn: canLoadCharts ? () => ApiDataService.chartsData(request) : skipToken,
    placeholderData: (prev) => prev,
    enabled: canLoadCharts,
  });

  // const queryMetadata = queryData?.meta;

  /* Show toast on query error */
  useEffect(() => {
    if (queryError) {
      handleApiError(queryError as ApiError, toastMessages.listApiData.error);
    }
  }, [queryError]);

  useEffect(() => {
    const defaultStationSelection = [...(filters.serialNumber || [])].slice(0, 5); // Set no more then 5 items

    setStationList([...(filters.serialNumber || [])]);
    setDraftStations(defaultStationSelection);
    setQueryState({
      serialNumber: defaultStationSelection,
      timeframe: Object.values(ApiDataChartTimeframe)[getMinTimeframe(defaultStationSelection.length)],
    });
  }, [filters.serialNumber]);

  useEffect(() => {
    if (queryState.startDate && queryState.endDate) {
      setQueryState({
        rangePreset: ApiDataChartRangePreset.custom,
      });
    }
  }, [queryState.startDate, queryState.endDate]);

  const handleRangeChange = (newValue: ApiDataChartRangePreset) => {
    setQueryState({
      rangePreset: newValue,
      startDate: undefined,
      endDate: undefined,
    });
  };

  const availableRangePresets = () => {
    const daysDiff = dayjs(filters.endDate).diff(filters.startDate, 'day');

    return Object.values(ApiDataChartRangePreset)
      .filter((preset) => {
        if (
          preset === ApiDataChartRangePreset.currentPage ||
          preset === ApiDataChartRangePreset.days90 ||
          preset === ApiDataChartRangePreset.months6
        ) {
          return true;
        }

        if (preset === ApiDataChartRangePreset.custom) {
          return queryState.rangePreset === ApiDataChartRangePreset.custom;
        }

        if (preset.startsWith('year')) {
          const years = parseInt(preset.slice(4), 10);

          if (years === 1) return daysDiff >= 180;

          return daysDiff >= years * 365;
        }

        return false;
      })
      .map((key: ApiDataChartRangePreset) => ({ label: t(`apiData:range_${key}` as const), value: key }));
  };

  const handleTimeFrame = (newValue: ApiDataChartTimeframe) => {
    setQueryState({ timeframe: newValue });
  };

  const handleStartDate = (newValue: string) => {
    setQueryState({ startDate: newValue });
  };

  const handleEndDate = (newValue: string) => {
    setQueryState({ endDate: newValue });
  };

  const handleStations = (newValue: string[]) => {
    const timeframes = Object.values(ApiDataChartTimeframe);
    const minimumTimeframe = getMinTimeframe(newValue.length);
    const currentTimeframe = queryState.timeframe ? timeframes.indexOf(queryState.timeframe) : 0;

    setQueryState({
      serialNumber: newValue,
      timeframe: timeframes[Math.max(currentTimeframe, minimumTimeframe)],
    });
  };

  /** Render **/
  return (
    <Box pad="none" flex elevation="none" {...boxProps} width="80%">
      <Box flex direction="column" gap="medium">
        <ChartCard
          pad="none"
          innerBoxProps={{
            direction: 'column',
            gap: 'small',
            pad: { top: 'medium', horizontal: 'medium', bottom: 'xsmall' },
          }}
          cardHeader={
            <CardHeader title={t('apiData:chartTitle')} icon={<BarChart size="24px" color="brand" />} pad="medium" />
          }
        >
          <Box justify="start" flex direction="row" gap="xsmall">
            <Select
              id="timeframe"
              label={t('apiData:timeframe')}
              placeholder={t('common:choose')}
              options={Object.keys(ApiDataChartTimeframe)
                .filter((_key, idx) => idx >= getMinTimeframe(queryState?.serialNumber?.length || 0))
                .map((key: string) => ({
                  label: t(`apiData:timeframe_${key as ApiDataChartTimeframe}` as const),
                  value: key,
                }))}
              value={queryState.timeframe}
              setValue={handleTimeFrame}
              fontFamily="Lato, sans-serif"
              hideOptionalText
            />
            <Box direction="column" gap="xsmall">
              <Select
                id="range"
                label={t('apiData:range')}
                placeholder={t('common:choose')}
                options={availableRangePresets()}
                value={queryState.rangePreset}
                setValue={handleRangeChange}
                fontFamily="Lato, sans-serif"
                hideOptionalText
              />
              <Box pad={{ left: 'xsmall' }}>
                <Text size="small" color="dark-5">
                  {t('apiData:rangeHelperText')}
                </Text>
              </Box>
            </Box>
            <Box direction="column" gap="xsmall">
              <Input
                id="startDateRange"
                label={t('apiData:startRange')}
                fill="horizontal"
                value={queryState.startDate}
                setValue={handleStartDate}
                componentType="date"
                dateBounds={['1900-01-01', queryState.endDate || filters.endDate || '2100-01-01']}
                width={{ width: pxToRem(200), max: pxToRem(200) }}
              />
              <Box pad={{ left: 'xsmall' }}>
                <Text size="small" color="dark-5">
                  {requestDates?.[0]?.format('YYYY-MM-DD')}
                </Text>
              </Box>
            </Box>
            <Box direction="column" gap="xsmall">
              <Input
                id="endDateRange"
                label={t('apiData:endRange')}
                fill="horizontal"
                value={queryState.endDate}
                setValue={handleEndDate}
                componentType="date"
                dateBounds={[queryState.startDate || filters.startDate || '1900-01-01', '2100-01-01']}
                width={{ width: pxToRem(200), max: pxToRem(200) }}
              />
              <Box pad={{ left: 'xsmall' }}>
                <Text size="small" color="dark-5">
                  {requestDates?.[1]?.format('YYYY-MM-DD')}
                </Text>
              </Box>
            </Box>
            <Select
              id="stations"
              label={t('apiData:stations')}
              placeholder={t('common:choose')}
              options={stationList}
              multiple
              values={draftStations}
              setValues={setDraftStations}
              onClose={() => handleStations(draftStations)}
              fontFamily="Lato, sans-serif"
              hideOptionalText
            />
          </Box>
        </ChartCard>
        {!hasAppliedFilters && (
          <ChartCard>
            <NoFiltersMessage />
          </ChartCard>
        )}
        {hasAppliedFilters && !isStationsSelected && (
          <ChartCard>
            <InfoBox justify="center">
              <Alert size="16px" />
              <Text fontFamily="Lato, sans-serif" size="large">
                {t('apiData:notificationStationSelect')}
              </Text>
            </InfoBox>
          </ChartCard>
        )}
        {(isLoading || isFetching) && <LoadingSpinner />}
        {!isLoading && !isFetching && canLoadCharts && queryData && (
          <ChartCard>
            <EnergyChart data={queryData?.dateChart} highlightColumn />
          </ChartCard>
        )}
        {!isLoading && !isFetching && canLoadCharts && queryData && (
          <ChartCard>
            <EnergyChart data={queryData?.stationChart} />
          </ChartCard>
        )}
      </Box>
    </Box>
  );
};

export type ChartsViewProps = BoxProps & {
  filters: ApiDataFilterQueryState;
  hasAppliedFilters: boolean;
  surfaceId?: string;
};
